import { useLayoutEffect } from 'react';

/** Marks the wrapper of the late-hydrated home sections in the server markup (see `deferred.css`). */
export const DEFERRED_ATTRIBUTE = 'data-deferred';
/** Selector of the wrapper (`routes/home.tsx` renders it, and the hydration gate looks for it). */
export const DEFERRED_SELECTOR = '[data-home-sections]';

/** Everything the motion primitives hide at the start and reveal on scroll (styles/motion.css). */
const REVEALABLE = '[data-reveal], [data-stack-reveal], [data-wave]';

/**
 * Called by the sections when they hydrate (or render for the first time): hand the page over from "as
 * pre-rendered" to "driven by the motion primitives". Whatever is on screen right now is marked revealed first, so
 * it does not blink out and back in; everything below the fold gets its usual reveal when it is scrolled to.
 * A layout effect: it runs before the browser paints the hydrated sections.
 */
export function useDeferredSection(): void {
  useLayoutEffect(() => {
    const wrapper = document.querySelector(DEFERRED_SELECTOR);
    if (!wrapper) return;
    // anything already in view? (a restored scroll position, a jump to a #section) — one read of the wrapper
    // tells the common case (sections far below the first screen) without touching the sections themselves
    if (wrapper.getBoundingClientRect().top < window.innerHeight) {
      for (const element of wrapper.querySelectorAll<HTMLElement>(REVEALABLE)) {
        if (element.dataset['revealed'] !== undefined) continue;
        const { top, bottom } = element.getBoundingClientRect();
        if (bottom > 0 && top < window.innerHeight) element.dataset['revealed'] = 'true';
      }
    }
    wrapper.removeAttribute(DEFERRED_ATTRIBUTE);
  }, []);
}
